import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css, ThemeContext } from 'styled-components';

import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { PictographPosition, VerticalOptionsStyle } from 'lib/interfaces/ThemeSerialized';
import { GovernmentIdEnabledIdClassByCountryEntry } from 'lib/interfaces/verificationConfig';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import { DisclaimerText } from 'verify/components/Disclaimer';
import GovernmentIdOptions from 'verify/components/GovernmentIdOptions';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedTitle from 'verify/components/ThemedTitle';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')<{ verticalOptionsStyle: VerticalOptionsStyle }>`
  margin-bottom: ${SPACINGS.base * 4}px;

  ${({ verticalOptionsStyle }) => {
    switch (verticalOptionsStyle) {
      case VerticalOptionsStyle.Default:
        return css`
          min-height: ${MIN_HEIGHTS.header};
        `;
      case VerticalOptionsStyle.Light:
        // This custom theme is specifically for DoorDash.
        return undefined;
    }
  }}
`;

interface VerifyGovernmentIdSelectViewProps {
  selectedCountryCode: CountryCode;
  enabledIdClassesByCountryEntries: GovernmentIdEnabledIdClassByCountryEntry[];
  handleRouteToSupport: () => void;
  handleSelectType: (type: GovernmentIdClass) => void;
  hasEnabledSkipUnknown: boolean;
  showDisclaimer: boolean;
}

export default function VerifyGovernmentIdSelectView(props: VerifyGovernmentIdSelectViewProps) {
  const {
    selectedCountryCode,
    handleSelectType,
    enabledIdClassesByCountryEntries,
    hasEnabledSkipUnknown,
    showDisclaimer,
  } = props;

  const [t] = useTranslation();
  const { governmentIdPictographPosition, verticalOptionsStyle, governmentIdSelectPictographUrl } =
    useContext(ThemeContext);

  const pictographNode = governmentIdSelectPictographUrl ? (
    <CenteredPictograph aria-label='government id prompt'>
      <CustomizablePictograph
        url='governmentIdSelectPictographUrl'
        height='governmentIdSelectPictographHeight'
        aria-label='government id prompt picture'
      />
    </CenteredPictograph>
  ) : null;

  return (
    <div>
      {governmentIdPictographPosition === PictographPosition.Start && pictographNode}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <Header verticalOptionsStyle={verticalOptionsStyle}>
        <ThemedTitle>{t('select.title')}</ThemedTitle>
        <ThemedParagraph>{t('select.prompt')}</ThemedParagraph>
      </Header>
      {governmentIdPictographPosition === PictographPosition.Center && pictographNode}
      <GovernmentIdOptions
        selectedCountryCode={selectedCountryCode}
        enabledIdClassesByCountryEntries={enabledIdClassesByCountryEntries}
        handleRouteToSupport={props.handleRouteToSupport}
        handleSelectType={handleSelectType}
      />
      {hasEnabledSkipUnknown && (
        <ThemedParagraph>
          <Trans
            i18nKey='select.or-other-identity-card'
            defaults='Don’t see your ID above? <0/><1>Click here to upload another document.</1>'
            components={[
              <br key={0} />,
              <a
                key={1}
                id='verify-government-id-select__other-identity-card'
                onClick={() => handleSelectType(undefined)}
              >
                Click here to upload another ID.
              </a>,
            ]}
          >
            Don’t see your ID above? Click here to upload another document.
          </Trans>
        </ThemedParagraph>
      )}
      {showDisclaimer && <DisclaimerText />}
    </div>
  );
}
